/**
 * Image Validator Utility
 * Client-side pre-validation for image uploads
 * Feature: User Management (002-user-management) - Phase 5 US4 - T107
 */

export const imageValidator = {
  allowedMimeTypes: ['image/jpeg', 'image/png'],

  /**
   * Validate a single image file
   * @param {File} file - Image file to validate
   * @returns {Promise<Object>} { valid: boolean, errors: string[], warnings: string[] }
   */
  async validateImage(file) {
    const errors = [];
    const warnings = [];

    // Check MIME type
    if (!this.allowedMimeTypes.includes(file.type)) {
      errors.push(`${file.name}: Invalid file type (${file.type}). Only JPEG and PNG images are allowed.`);
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  },

  /**
   * Get image dimensions by loading into an Image element
   * @param {File} file - Image file
   * @returns {Promise<Object>} { width: number, height: number }
   */
  getImageDimensions(file) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({
          width: img.naturalWidth,
          height: img.naturalHeight
        });
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };

      img.src = url;
    });
  },

  /**
   * Validate multiple image files
   * @param {FileList|Array} files - Files to validate
   * @returns {Promise<Object>} { valid: number, invalid: number, errors: string[], warnings: string[] }
   */
  async validateBatch(files) {
    const allErrors = [];
    const allWarnings = [];
    let validCount = 0;
    let invalidCount = 0;

    for (const file of files) {
      const result = await this.validateImage(file);
      if (result.valid) {
        validCount++;
      } else {
        invalidCount++;
      }
      allErrors.push(...result.errors);
      allWarnings.push(...result.warnings);
    }

    return {
      valid: validCount,
      invalid: invalidCount,
      errors: allErrors,
      warnings: allWarnings
    };
  }
};

export default imageValidator;
